import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

/**
 * Drag-to-pan by translating inner content (no scrollbars).
 * Skips pan when disabled, modifier keys are held, or the event starts on an ignored element.
 */
@Directive({
  selector: '[appDragPan]',
})
export class DragPanDirective implements OnChanges, OnDestroy {
  /** When false, panning is off. */
  @Input('appDragPan') enabled = true;

  /** Disables pan while true (e.g. CTRL held for hotspot placement). */
  @Input() appDragPanDisabled = false;

  /** Closest-match selector; pointerdown on these elements does not start a pan. */
  @Input() appDragPanIgnore = '.dot';

  /** Selector for the element that receives translate(); defaults to the first child. */
  @Input() appDragPanContent = '';

  /** Increment or change to reset pan offset to zero. */
  @Input() appDragPanReset: number | string = 0;

  /** Fired after the panned content moves so parents can re-project overlays. */
  @Output() appDragPanChange = new EventEmitter<void>();

  @HostBinding('class.is-panning') isPanning = false;

  private panning = false;
  private offsetX = 0;
  private offsetY = 0;
  private panStartOffsetX = 0;
  private panStartOffsetY = 0;
  private startClientX = 0;
  private startClientY = 0;

  constructor(private readonly el: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appDragPanReset'] && !changes['appDragPanReset'].firstChange) {
      this.resetPan();
    }
    if (changes['enabled'] && !this.enabled) {
      this.resetPan();
    }
  }

  ngOnDestroy(): void {
    this.removeDocumentListeners();
    this.endPan();
  }

  resetPan(): void {
    this.offsetX = 0;
    this.offsetY = 0;
    this.applyTransform();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (!this.enabled || this.appDragPanDisabled || event.button !== 0) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest(this.appDragPanIgnore)) {
      return;
    }

    const content = this.getContentEl();
    if (!content) {
      return;
    }

    const host = this.el.nativeElement;
    const hostRect = host.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const canPanX = contentRect.width > hostRect.width + 1;
    const canPanY = contentRect.height > hostRect.height + 1;
    if (!canPanX && !canPanY) {
      return;
    }

    this.panning = true;
    this.isPanning = true;
    this.panStartOffsetX = this.offsetX;
    this.panStartOffsetY = this.offsetY;
    this.startClientX = event.clientX;
    this.startClientY = event.clientY;

    document.addEventListener('pointermove', this.boundDocumentMove);
    document.addEventListener('pointerup', this.boundDocumentUp);
    document.addEventListener('pointercancel', this.boundDocumentUp);
    event.preventDefault?.();
  }

  private boundDocumentMove = (event: PointerEvent) => {
    if (!this.panning) {
      return;
    }
    this.offsetX = this.panStartOffsetX + (event.clientX - this.startClientX);
    this.offsetY = this.panStartOffsetY + (event.clientY - this.startClientY);
    this.applyTransform();
    this.appDragPanChange.emit();
  };

  private boundDocumentUp = (_event: PointerEvent) => {
    if (!this.panning) {
      return;
    }
    this.removeDocumentListeners();
    this.endPan();
    this.appDragPanChange.emit();
  };

  private getContentEl(): HTMLElement | null {
    if (this.appDragPanContent) {
      return this.el.nativeElement.querySelector(this.appDragPanContent);
    }
    return this.el.nativeElement.firstElementChild as HTMLElement | null;
  }

  private applyTransform(): void {
    const content = this.getContentEl();
    if (!content) {
      return;
    }
    content.style.transform = `translate(${this.offsetX}px, ${this.offsetY}px)`;
  }

  private removeDocumentListeners(): void {
    document.removeEventListener('pointermove', this.boundDocumentMove);
    document.removeEventListener('pointerup', this.boundDocumentUp);
    document.removeEventListener('pointercancel', this.boundDocumentUp);
  }

  private endPan(): void {
    this.panning = false;
    this.isPanning = false;
  }
}
